import React, {useCallback, useEffect, useState} from 'react';


import {CopyButton} from 'src/components/copy-button';
import {WidthItem} from 'src/components/width-item';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {GRANT_STATUS_CAPTIONS} from 'src/modules/entitlements/constants';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getGrantResources} from 'src/openapi/admin/apis/GrantResourcesApi';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {AddGrantSuggest} from './add-grant-suggest';

import type {EntitlementsResource} from 'src/openapi/admin/models/EntitlementsResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {Pagination} from 'src/openapi/admin/models/Pagination';


type Props = {
    grantsIds: string[],
    onAddGrant: (grantId: string) => void,
    onRemoveGrant: (grantId: string) => void,
};

type DisplayedGrant = {grant: Grant, resources: EntitlementsResource[]};

export const RoleGrants = ({onAddGrant, onRemoveGrant, grantsIds}: Props) => {
    const apiDispatch = useAppDispatch();

    const [{currentPage, pageSize}, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 5});
    const [displayedGrants, setDisplayedGrants] = useState<DisplayedGrant[]>([]);
    const [isFetching, setIsFetching] = useState(true);
    const [expandedGrants, setExpandedGrants] = useState<string[]>([]);

    const loadGrants = useCallback(async (ids: string[], pNumber: number, pSize: number) => {
        const requestBody = ids.slice((pNumber - 1) * pSize, pNumber * pSize);
        setExpandedGrants(prevState => prevState.filter(id => ids.includes(id)));
        setIsFetching(true);
        const grantsResponse = await apiDispatch(getGrantsByIds({requestBody}));

        const loadedGrants: DisplayedGrant[] = [];
        if (grantsResponse.payload.success) {
            for (const id of requestBody) {
                const grants = Object.values(grantsResponse.payload.data);

                const loadedGrant: DisplayedGrant = {
                    grant: grants.find(({id: grantId}) => grantId === id) || {
                        id,
                        name: 'Grant not found',
                        description: '',
                        status: GrantStatus.DENY,
                        requiredApprovals: false,
                    },
                    resources: [],
                };
                const resourcesResponse = await apiDispatch(getGrantResources({
                    grantId: id,
                    pageSize: 250,
                    pageNumber: 1,
                }));

                if (resourcesResponse.payload.success) {
                    loadedGrant.resources = resourcesResponse.payload.data.resources;
                }
                loadedGrants.push(loadedGrant);
            }

            setDisplayedGrants(loadedGrants);
        }
        setIsFetching(false);
    }, [apiDispatch]);

    useEffect(() => {
        loadGrants(grantsIds, currentPage, pageSize);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadGrants(current.grantsIds, current.currentPage, current.pageSize);
        }
    }, {grantsIds, pageSize, currentPage});

    const removeResource = useCallback((grantId: string) => {
        const totalPages = Math.ceil((grantsIds.length - 1) / pageSize);

        if ((grantsIds.length - 1) && currentPage > totalPages) {
            setPagination({currentPage: totalPages, pageSize});
        }

        onRemoveGrant(grantId);
    }, [grantsIds, pageSize, currentPage, onRemoveGrant]);

    const expandGrant = useCallback((grantId: string) => {
        setExpandedGrants([...expandedGrants, grantId]);
    }, [expandedGrants]);

    const collapseGrant = useCallback((grantId: string) => {
        setExpandedGrants(expandedGrants.filter(id => id !== grantId));
    }, [expandedGrants]);

    return (
        <FormSection headline="Grant(s) in this Role">
            <Group direction="v" alignItems="normal" gap="m">
                <AddGrantSuggest
                    id="add-grant-suggest"
                    onSearch={onAddGrant}
                    selectedGrants={grantsIds}
                />
                {grantsIds.length ? (
                    <WidthItem width={1200}>
                        <BoxLoading loading={isFetching}>
                            <Group direction="v">
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell width={200}>Name</Cell>
                                        <Cell>Description</Cell>
                                        <Cell width={200}>Grant ID</Cell>
                                        <Cell width={100}>Status</Cell>
                                        <Cell width={200}>Restrictions</Cell>
                                        <Cell width={300}>Resources</Cell>
                                        <Cell width={45}/>
                                    </Head>
                                    <Body>
                                        {Object.values(displayedGrants).map(({grant, resources}) => {
                                            const {id, name, description, status, daysToDelay} = grant;

                                            return (
                                                <Row key={`${grant.id}_${name}_${description}`}>
                                                    <Cell>
                                                        <Group gap="xs">
                                                            {resources.length > 1 ? (
                                                                <BasicIconButton
                                                                    iconProps={{
                                                                        type: expandedGrants.includes(id)
                                                                            ? Icons.CHEVRON_DOWN
                                                                            : Icons.CHEVRON_UP,
                                                                        size: Sizes.Size_10,
                                                                    }}
                                                                    onClick={expandedGrants.includes(id)
                                                                        ? () => collapseGrant(id)
                                                                        : () => expandGrant(id)}
                                                                />
                                                            ) : <WidthItem width={14}/>}
                                                            <EllipsisTextHint title={name}>
                                                                {name}
                                                            </EllipsisTextHint>
                                                        </Group>
                                                    </Cell>
                                                    <Cell>
                                                        <EllipsisTextHint title={description}>
                                                            {description}
                                                        </EllipsisTextHint>
                                                    </Cell>
                                                    <Cell>
                                                        <Group gap="s" alignItems="center" justify="space-between">
                                                            <WidthItem width={125}>
                                                                <EllipsisTextHint title={grant.id}>
                                                                    {grant.id}
                                                                </EllipsisTextHint>
                                                            </WidthItem>
                                                            <CopyButton copiedText={grant.id} entityName="Grant ID"/>
                                                        </Group>
                                                    </Cell>
                                                    <Cell>{GRANT_STATUS_CAPTIONS[status]}</Cell>
                                                    <Cell>
                                                        {daysToDelay ? `Days to delay: ${daysToDelay}` : '--'}
                                                    </Cell>
                                                    <Cell>
                                                        {resources.length > 1 && (
                                                            <div>
                                                                <b>{pluralize(
                                                                    `${resources.length} resource[s] in this grant`,
                                                                    resources.length,
                                                                )}
                                                                </b>
                                                                {expandedGrants.includes(id) && (
                                                                    <Item className="group-item pdx-s">
                                                                        <Group direction="v" gap="xs">
                                                                            {resources.map((
                                                                                {name: resourceName, externalId},
                                                                            ) => (
                                                                                <EllipsisTextHint
                                                                                    key={externalId}
                                                                                    title={resourceName}>
                                                                                    {resourceName}
                                                                                </EllipsisTextHint>
                                                                            ))}
                                                                        </Group>
                                                                    </Item>
                                                                )}
                                                            </div>
                                                        )}
                                                        {resources.length === 1 && (
                                                            <EllipsisTextHint
                                                                key={resources[0].externalId}
                                                                title={resources[0].name}>
                                                                {resources[0].name}
                                                            </EllipsisTextHint>
                                                        )}
                                                    </Cell>
                                                    <Cell>
                                                        <BasicIconButton
                                                            iconProps={{type: Icons.TRASH_LIGHT}}
                                                            onClick={() => removeResource(id)}
                                                            title="Remove Grant"
                                                        />
                                                    </Cell>
                                                </Row>
                                            );
                                        })}
                                    </Body>
                                </Table>
                                <PaginationStripe
                                    totalCount={grantsIds.length}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    onPaginationChange={setPagination}
                                    sizes={[5, 10]}
                                />
                            </Group>
                        </BoxLoading>
                    </WidthItem>
                ) : null}
            </Group>
        </FormSection>
    );
};
